import { Action, Module, Mutation } from "vuex-class-modules"

import { BaseModule } from "@/models/BaseModule"
import {
  AiRequestLogDetail,
  AiRequestLogPreview,
  AiRequestStats,
  FilterAiActivityLog,
  ServerResponse
} from "@/models"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { parsingServerResponseContent } from "@/utils/helpers"

import { Modules } from "../modules"
import { store } from "../store"

export enum AiActivityLogActions {
  GET_AI_ACTIVITY_LOG_ACCOUNT = "GET_AI_ACTIVITY_LOG_ACCOUNT",
  GET_AI_ACTIVITY_LOG_SUPERADMIN = "GET_AI_ACTIVITY_LOG_SUPERADMIN",
  GET_AI_ACTIVITY_LOG_ACCOUNT_STATS = "GET_AI_ACTIVITY_LOG_ACCOUNT_STATS",
  GET_AI_ACTIVITY_LOG_SUPERADMIN_STATS = "GET_AI_ACTIVITY_LOG_SUPERADMIN_STATS",
  GET_AI_ACTIVITY_LOG_ACCOUNT_DETAIL = "GET_AI_ACTIVITY_LOG_ACCOUNT_DETAIL",
  GET_AI_ACTIVITY_LOG_SUPERADMIN_DETAIL = "GET_AI_ACTIVITY_LOG_SUPERADMIN_DETAIL",
  CLEAR_AI_ACTIVITY_LOG_DETAIL = "CLEAR_AI_ACTIVITY_LOG_DETAIL"
}

export interface AiActivityFilterOption {
  id: string
  name: string
  pictureUrl?: string | null
}

@Module
class AiActivityLogModule extends BaseModule {
  requests: AiRequestLogPreview[] = []
  response: ServerResponse<AiRequestLogPreview> = new ServerResponse<AiRequestLogPreview>()
  stats = new AiRequestStats()
  detail: AiRequestLogDetail | null = null

  @Mutation
  setRequestsResponse(response: ServerResponse<AiRequestLogPreview>) {
    const requests = response.content.map(request => new AiRequestLogPreview(request))
    this.requests = parsingServerResponseContent({
      originalContent: this.requests,
      serverResponseContent: requests,
      numberOfPage: response.number
    })
    this.response = new ServerResponse({ ...response, content: this.requests })
  }

  @Mutation
  setStats(stats: AiRequestStats) {
    this.stats = new AiRequestStats(stats)
  }

  @Mutation
  setDetail(detail: AiRequestLogDetail | null) {
    this.detail = detail
  }

  @Action
  async [AiActivityLogActions.GET_AI_ACTIVITY_LOG_ACCOUNT]({
    accountId,
    filter
  }: {
    accountId: string
    filter: Pick<FilterAiActivityLog, "getActivityLogJsonObj">
  }) {
    const data = await apiService.get<ServerResponse<AiRequestLogPreview>>(
      Endpoint.AI_ACTIVITY_LOG_ACCOUNT(accountId),
      filter.getActivityLogJsonObj()
    )
    return this.handleResponse<ServerResponse<AiRequestLogPreview>>(data, response => {
      this.setRequestsResponse(response)
      return response
    })
  }

  @Action
  async [AiActivityLogActions.GET_AI_ACTIVITY_LOG_SUPERADMIN](
    filter: Pick<FilterAiActivityLog, "getActivityLogJsonObj">
  ) {
    const data = await apiService.get<ServerResponse<AiRequestLogPreview>>(
      Endpoint.AI_ACTIVITY_LOG_SUPERADMIN(),
      filter.getActivityLogJsonObj(true)
    )
    return this.handleResponse<ServerResponse<AiRequestLogPreview>>(data, response => {
      this.setRequestsResponse(response)
      return response
    })
  }

  @Action
  async [AiActivityLogActions.GET_AI_ACTIVITY_LOG_ACCOUNT_STATS]({
    accountId,
    filter
  }: {
    accountId: string
    filter: Pick<FilterAiActivityLog, "getStatsJsonObj">
  }) {
    const data = await apiService.get<AiRequestStats>(
      Endpoint.AI_ACTIVITY_LOG_ACCOUNT_STATS(accountId),
      filter.getStatsJsonObj()
    )
    return this.handleResponse<AiRequestStats>(data, stats => {
      this.setStats(stats)
      return this.stats
    })
  }

  @Action
  async [AiActivityLogActions.GET_AI_ACTIVITY_LOG_SUPERADMIN_STATS](
    filter: Pick<FilterAiActivityLog, "getStatsJsonObj">
  ) {
    const data = await apiService.get<AiRequestStats>(
      Endpoint.AI_ACTIVITY_LOG_SUPERADMIN_STATS(),
      filter.getStatsJsonObj(true)
    )
    return this.handleResponse<AiRequestStats>(data, stats => {
      this.setStats(stats)
      return this.stats
    })
  }

  @Action
  async [AiActivityLogActions.GET_AI_ACTIVITY_LOG_ACCOUNT_DETAIL]({
    accountId,
    requestId
  }: {
    accountId: string
    requestId: string
  }) {
    const data = await apiService.get<AiRequestLogDetail>(
      Endpoint.AI_ACTIVITY_LOG_ACCOUNT_DETAIL(accountId, requestId)
    )
    return this.handleResponse<AiRequestLogDetail>(data, detail => {
      this.setDetail(detail)
      return detail
    })
  }

  @Action
  async [AiActivityLogActions.GET_AI_ACTIVITY_LOG_SUPERADMIN_DETAIL](requestId: string) {
    const data = await apiService.get<AiRequestLogDetail>(
      Endpoint.AI_ACTIVITY_LOG_SUPERADMIN_DETAIL(requestId)
    )
    return this.handleResponse<AiRequestLogDetail>(data, detail => {
      this.setDetail(detail)
      return detail
    })
  }

  @Action
  [AiActivityLogActions.CLEAR_AI_ACTIVITY_LOG_DETAIL]() {
    this.setDetail(null)
  }
}

export const aiActivityLogModule = new AiActivityLogModule({
  store,
  name: Modules.AI_ACTIVITY_LOG
})
